import {useState, useEffect} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {toast} from 'react-toastify';
import './Notifications.scss';
import {
  useNotificationPreferences,
  useUpdateNotificationPreferences,
} from '../../../hooks/useNotifications';
import FboCustomization from './FboCustomization';

const Toggle = ({checked, onChange}) => (
  <button
    type='button'
    className={`notif-toggle${checked ? ' notif-toggle--on' : ''}`}
    onClick={() => onChange(!checked)}
    aria-checked={checked}
    role='switch'
  >
    <span className='notif-toggle__thumb' />
  </button>
);

const NotificationRow = ({
  title,
  description,
  checked,
  onChange,
  alertBadge,
}) => (
  <div className='notif-row'>
    <div className='notif-row__text'>
      <p className='notif-row__title'>
        {title}
        {alertBadge && <span className='notif-row__alert-badge'>ALERT</span>}
      </p>
      <p className='notif-row__desc'>{description}</p>
    </div>
    <Toggle checked={checked} onChange={onChange} />
  </div>
);

const DEFAULT_ROW_META = {
  notify_corrective_action_created: {
    title: 'Corrective Action Created',
    description:
      'Triggered when a corrective action is created following a non-conformance identified during an inspection.',
  },
  notify_corrective_action_closed: {
    title: 'Corrective Action Closed',
    description:
      'Triggered when a corrective action has been completed and closed.',
  },
  notify_differential_pressure_alert: {
    title: 'Differential Pressure Alert',
    description:
      'Triggered when a differential pressure (DP) reading exceeds the acceptable range.',
    alertBadge: true,
  },
  notify_equipment_decommissioned: {
    title: 'Equipment Decommissioned',
    description:
      'Triggered when equipment is decommissioned, either automatically (for example, due to inspection results or missed inspections) or manually by a user.',
  },
  notify_missed_inspections: {
    title: 'Missed Inspections',
    description: 'Triggered when inspections are missed.',
  },
  notify_nozzle_fueling_pressure_alert: {
    title: 'Nozzle Fueling Pressure Alert',
    description:
      'Triggered when a nozzle fueling pressure reading is outside the acceptable range.',
    alertBadge: true,
  },
  notify_partial_inspections: {
    title: 'Partial Inspections',
    description:
      'Triggered when an inspection is started but not fully completed.',
  },
  notify_sump_sample_threshold: {
    title: 'Sump Sample Threshold',
    description:
      'Triggered when a sump sample reading exceeds the configured threshold value. Thresholds are configured in the FBO settings.',
    alertBadge: true,
  },
  notify_tank_condition_alert: {
    title: 'Tank Condition Alert',
    description:
      'Triggered when a tank condition reading is outside the acceptable range.',
    alertBadge: true,
  },
  notify_upcoming_calibrations: {
    title: 'Upcoming Calibration Date',
    description: 'Calibration due in the next 30 days.',
  },
  notify_upcoming_oos: {
    title: 'Upcoming Out of Service',
    description:
      'Triggered when equipment across multiple FBOs is approaching an out-of-service condition. This notification provides a consolidated summary of equipment that will soon be taken out of service.',
  },
};

const CORPORATE_ROW_META = {
  notify_out_of_service: {
    title: 'Equipment Out of Service Summary',
    description:
      'Triggered when one or more pieces of equipment are placed out of service in an FBO. This notification provides a consolidated summary of all affected equipment across multiple FBOs.',
  },
};

const Notifications = () => {
  const queryClient = useQueryClient();
  const {data: notifications} = useNotificationPreferences();
  const {corporate, agencies, defaults} = notifications ?? {};
  const {mutate: updatePreferences} = useUpdateNotificationPreferences();

  const [localCorporate, setLocalCorporate] = useState({});
  const [localDefaults, setLocalDefaults] = useState({});
  const [showApplyConfirm, setShowApplyConfirm] = useState(false);

  useEffect(() => {
    if (corporate) setLocalCorporate(corporate);
  }, [corporate]);

  useEffect(() => {
    if (defaults) setLocalDefaults(defaults);
  }, [defaults]);

  const handleCorporateChange = (key, value) => {
    const previous = localCorporate;
    const updated = {...localCorporate, [key]: value};
    setLocalCorporate(updated);
    updatePreferences(
      {corporate: updated},
      {
        onSuccess: data => {
          queryClient.invalidateQueries(['notificationPreferences']);
          toast.success(data?.message ?? 'Preferences saved');
        },
        onError: error => {
          setLocalCorporate(previous);
          toast.error(
            error?.response?.data?.message ?? 'Failed to save preferences',
          );
        },
      },
    );
  };

  const handleDefaultsChange = (key, value) => {
    const previous = localDefaults;
    const updated = {...localDefaults, [key]: value};
    setLocalDefaults(updated);
    updatePreferences(
      {defaults: updated},
      {
        onSuccess: data => {
          queryClient.invalidateQueries(['notificationPreferences']);
          toast.success(data?.message ?? 'Preferences saved');
        },
        onError: error => {
          setLocalDefaults(previous);
          toast.error(
            error?.response?.data?.message ?? 'Failed to save preferences',
          );
        },
      },
    );
  };

  const confirmApplyDefaultsToAll = () => {
    if (!agencies?.length) return;
    updatePreferences(
      {
        agencies: agencies.map(a => ({
          agency_id: a.agency_id,
          use_default: true,
          notifications: null,
        })),
      },
      {
        onSuccess: data => {
          queryClient.invalidateQueries(['notificationPreferences']);
          toast.success(data?.message ?? 'Defaults applied to all FBOs');
          setShowApplyConfirm(false);
        },
        onError: error => {
          toast.error(
            error?.response?.data?.message ??
              'Failed to apply defaults to all FBOs',
          );
          setShowApplyConfirm(false);
        },
      },
    );
  };

  return (
    <div className='notif-wrapper'>
      <div className='notif-header'>
        <p className='notif-header__title'>Email Notifications</p>
        <p className='notif-header__subtitle'>
          Manage your notification preferences by selecting which notifications
          you would like to receive.
        </p>
      </div>

      <div className='notif-section'>
        <>
          {corporate && (
            <>
              <div className='notif-section__header'>
                <span className='notif-section__title'>
                  Consolidated Notifications
                </span>
                <span className='notif-section__badge notif-section__badge--consolidated'>
                  CONSOLIDATED
                </span>
              </div>
              <p className='notif-section__desc'>
                These notifications provide a consolidated summary across
                multiple FBOs and cannot be configured separately for each FBO.
              </p>

              <div className='notif-rows'>
                {Object.entries(CORPORATE_ROW_META).map(([key, meta]) => (
                  <NotificationRow
                    key={key}
                    title={meta.title}
                    description={meta.description}
                    checked={localCorporate[key] ?? false}
                    onChange={val => handleCorporateChange(key, val)}
                  />
                ))}
              </div>
            </>
          )}
        </>
      </div>

      <div className='notif-section notif-section--spaced'>
        <>
          {defaults && (
            <>
              <div className='notif-section__header'>
                <span className='notif-section__title'>
                  Default notifications
                </span>
                <span className='notif-section__badge notif-section__badge--all-fbos'>
                  APPLIED TO ALL FBOS
                </span>
              </div>
              <p className='notif-section__desc'>
                These settings apply to every FBO by default. FBOs set to
                Override below will use their own custom list instead.
              </p>

              <div className='notif-rows'>
                {Object.entries(DEFAULT_ROW_META).map(([key, meta]) => (
                  <NotificationRow
                    key={key}
                    title={meta.title}
                    description={meta.description}
                    alertBadge={meta.alertBadge}
                    checked={localDefaults[key] ?? false}
                    onChange={val => handleDefaultsChange(key, val)}
                  />
                ))}
              </div>
            </>
          )}
        </>
      </div>

      <>
        {agencies && (
          <>
            <FboCustomization
              agencies={agencies}
              defaults={localDefaults}
              onUpdate={updatePreferences}
              onApplyToAll={() => setShowApplyConfirm(true)}
            />
          </>
        )}
      </>

      {showApplyConfirm && (
        <div className='modal'>
          <div className='modal__content'>
            <div className='modal__content-head'>
              <p>Apply defaults to all FBOs</p>
              <img
                className='modal__content-exit'
                src='/assets/icons/close.svg'
                alt='close'
                onClick={() => setShowApplyConfirm(false)}
              />
            </div>
            <div
              className='modal__content-body'
              style={{display: 'flex', justifyContent: 'center'}}
            >
              This will reset all FBOs to use the default notification settings,
              removing any per-FBO overrides. Continue?
            </div>
            <div className='modal__content-bottom'>
              <button
                className='close'
                onClick={() => setShowApplyConfirm(false)}
              >
                Cancel
              </button>
              <button className='save' onClick={confirmApplyDefaultsToAll}>
                Apply
              </button>
            </div>
          </div>
          <div
            className='modal__bg'
            onClick={() => setShowApplyConfirm(false)}
          />
        </div>
      )}
    </div>
  );
};

export default Notifications;
